import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { deserialize } from 'santee-dcts';
import { Settings } from '../../core/settings';
import { FilterFieldValue } from '../../covance-filter';
import { AdminEQueryEscalationsFilterResolver } from './admin-e-query-escalations-filter-resolver';
import { AdminEQueryEscalation, AdminEQueryEscalationsReadModel } from './models/admin-e-query-escalation';
import { EQueryEscalationLevel } from './models/e-query-escalation-level';
import { AdminEQueryEscalationResponse } from './admin-e-query-escalation-response';

interface SearchValues {
    readonly email: string;
    readonly eQueryId: string;
    readonly accessionNumber: string;
}

interface CreateEscalationBody {
    readonly accessionId: number;
    readonly email: string;
    readonly escalationLevel: EQueryEscalationLevel;
    readonly reason: string;
}

@Injectable()
export class AdminEQueryEscalationsStorage {
    private readonly refresher$ = new Subject<void>();

    constructor(
        private readonly http: HttpClient,
        private readonly settings: Settings,
        private readonly filterResolver: AdminEQueryEscalationsFilterResolver,
    ) {}

    public getEscalations(searchValues: SearchValues, filterFields: FilterFieldValue[]): Observable<AdminEQueryEscalation[]> {
        if (this.areSearchFieldsEmpty(searchValues)) {
            return of([]);
        }

        let params = this.prepareSearchFieldsParams(searchValues);
        params = this.prepareFilterParams(params, filterFields);

        return this.refresher$.pipe(
            startWith(null),
            switchMap(() =>
                this.http
                    .get(`${this.settings.apiUrl}/admin/e-query-escalations`, { params })
                    .pipe(
                        map(mails => deserialize(mails, AdminEQueryEscalationsReadModel).escalations),
                        startWith(null),
                    )));
    }

    public createEscalation(body: CreateEscalationBody): Promise<AdminEQueryEscalationResponse> {
        return this.http
            .post(`${this.settings.apiUrl}/admin/e-query-escalations`, body)
            .toPromise()
            .then(x => deserialize(x, AdminEQueryEscalationResponse));
    }

    public reloadEscalations() {
        this.refresher$.next();
    }

    public deleteEscalation(escalation: AdminEQueryEscalation, auditTrailReason: string): Promise<AdminEQueryEscalationResponse> {
        return this.http
            .delete(`${this.settings.apiUrl}/admin/e-query-escalations/${escalation.escalationId}`, { params: { 'auditTrailReason': auditTrailReason } })
            .toPromise()
            .then(x => deserialize(x, AdminEQueryEscalationResponse));
    }

    private areSearchFieldsEmpty(searchValues: SearchValues) {
        return !searchValues.email
            && !searchValues.eQueryId
            && !searchValues.accessionNumber;
    }

    private prepareSearchFieldsParams(searchValues: SearchValues): HttpParams {
        let httpParams = new HttpParams();

        if (searchValues.email) {
            httpParams = httpParams.append('email', searchValues.email);
        }

        if (searchValues.eQueryId) {
            httpParams = httpParams.append('eQueryId', searchValues.eQueryId);
        }

        if (searchValues.accessionNumber) {
            httpParams = httpParams.append('accessionNumber', searchValues.accessionNumber);
        }

        return httpParams;
    }

    private prepareFilterParams(httpParams: HttpParams, filterParameters: FilterFieldValue[]) {
        if (!filterParameters) {
            return httpParams;
        }

        filterParameters
            .filter(p => p.value)
            .forEach((filterParam) => {
                const filterValue = filterParam.value;
                const paramName = filterParam.def.queryParam;

                if (paramName === this.filterResolver.escalationSentTimeQueryParam) {
                    httpParams = httpParams.append(`${this.filterResolver.escalationSentTimeQueryParam}FromString`, (filterValue.dateBegin) ? encodeURIComponent(moment(filterValue.dateBegin).format()) : '');
                    httpParams = httpParams.append(`${this.filterResolver.escalationSentTimeQueryParam}ToString`, (filterValue.dateEnd) ? encodeURIComponent(moment(filterValue.dateEnd).format()) : '');
                    return;
                }

                if (Array.isArray(filterValue)) {
                    filterValue.filter(v => !!v)
                        .forEach(v => httpParams = httpParams.append(paramName, v.key || v));
                } else {
                    httpParams = httpParams.append(paramName, filterValue.key || filterValue);
                }
            });

        return httpParams;
    }
}
